import { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button, Modal } from '~/components';
import { useProjectContext } from '~/hooks';
import { getIngestionUploadUrl, startIngestion } from '~/api/ingestion';
import { useDocumentIngestionProgress } from './DocumentIngestionProgressProvider';
import IngestionHistoryTable from './IngestionHistoryTable';

const ACCEPTED_LABEL = 'PDF, XLSX, or XLS (max 10MB)';
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export default function DocumentIngestion() {
  const { project } = useProjectContext();
  const projectId = project?.id ?? '';
  const progress = useDocumentIngestionProgress();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [additionalContext, setAdditionalContext] = useState('');

  const status = progress?.status ?? 'idle';
  const statusMessage = progress?.statusMessage ?? null;
  const errorMessage = progress?.errorMessage ?? null;
  const isProcessing = status === 'uploading' || status === 'processing';

  const clearImportConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const { mutateAsync: runUploadAndStart } = useMutation(
    async (file: File) => {
      const { presignedUrl, s3Key } = await getIngestionUploadUrl(projectId, file.name);
      await axios.put(presignedUrl, file);
      const idempotencyKey =
        crypto.randomUUID?.() ??
        `ingest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { apiEvent } = await startIngestion(projectId, {
        s3Key,
        fileName: file.name,
        idempotencyKey,
        additionalContext: additionalContext.trim() || undefined,
      });
      return apiEvent.id;
    },
    {
      onMutate: () => {
        progress?.setUploading();
      },
      onSuccess: (id) => {
        setSelectedFile(null);
        setAdditionalContext('');
        progress?.startTracking(id);
      },
      onError: (err: any) => {
        const message =
          err?.response?.data?.message ?? err?.message ?? 'Upload failed.';
        toast.error(message);
        progress?.setError(message);
      },
    }
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ],
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length) {
        const sizeReject = rejectedFiles.find((f) =>
          (f.errors as any)?.some((e: any) => e.code === 'file-too-large')
        );
        toast.error(
          sizeReject
            ? 'File must be 10MB or smaller.'
            : `Please upload a single file of type ${ACCEPTED_LABEL}.`
        );
        return;
      }
      if (acceptedFiles?.length === 1) {
        setSelectedFile(acceptedFiles[0]);
      }
    },
    disabled: isProcessing || confirmOpen,
  });

  const handleSubmitImport = useCallback(() => {
    if (!selectedFile) {
      toast.error('Please select a file first.');
      return;
    }
    setConfirmOpen(true);
  }, [selectedFile]);

  const handleConfirmImport = useCallback(async () => {
    const file = selectedFile;
    clearImportConfirm();
    if (!file) return;
    try {
      await runUploadAndStart(file);
    } catch {
      // Mutation onError shows toast
    }
  }, [selectedFile, clearImportConfirm, runUploadAndStart]);

  if (!projectId) return null;

  return (
    <>
      {confirmOpen && selectedFile && (
        <Modal closeModal={clearImportConfirm}>
          <Modal.Title>Overwrite existing project data?</Modal.Title>
          <Modal.Content>
            <p>
              Values extracted from the document will be written to this project. Any fields the import
              recognizes (including the project name, if present in the file) will replace what is
              currently on the worksheet.
            </p>
            <p style={{ marginBottom: 0 }}>
              <strong>File:</strong> {selectedFile.name}
            </p>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={clearImportConfirm}>
              Cancel
            </Button>
            <Button primary onClick={() => void handleConfirmImport()}>
              Submit import
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <div className="card">
        <h3>Import from document</h3>

        {isProcessing && (
          <>
            <style>{'@keyframes cfd-spin { to { transform: rotate(360deg); } }'}</style>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 'var(--small)' }}>
              <div style={{ flexShrink: 0, paddingTop: 6, width: 10 }}>
                <span
                  style={{
                    position: 'relative',
                    display: 'block',
                    width: 17,
                    height: 17,
                    marginTop: -4,
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      position: 'absolute',
                      top: 4.5,
                      left: 4.5,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#f2711c',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 17,
                      height: 17,
                      borderRadius: '50%',
                      border: '2px solid #ffd8b5',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 17,
                      height: 17,
                      borderRadius: '50%',
                      border: '2px solid rgba(0, 0, 0, 0)',
                      borderTopColor: '#f2711c',
                      borderRightColor: '#f2711c',
                      boxSizing: 'border-box',
                      transformOrigin: '50% 50%',
                      willChange: 'transform',
                      animation: 'cfd-spin 0.8s linear infinite',
                    }}
                  />
                </span>
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '1.125rem' }}>
                  Importing data from document
                </p>
                <p style={{ marginBottom: '4px' }}>
                  {statusMessage ?? 'Starting…'}
                </p>
                <p style={{ fontSize: '12px', color: '#777' }}>
                  Approx. 1 minute remaining. You can safely leave this page.
                </p>
              </div>
            </div>
          </>
        )}

        {status === 'error' && errorMessage && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 'var(--small)' }}>
            <span
              style={{
                display: 'block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: '#db2828',
                marginTop: 6,
                flexShrink: 0,
              }}
            />
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '1rem' }}>Import failed</p>
              <p style={{ margin: '2px 0 0' }}>{errorMessage}</p>
              <p style={{ margin: '6px 0 0' }}>
                <button
                  type="button"
                  onClick={() => progress?.dismissError()}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'var(--color-primary)',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Dismiss
                </button>
              </p>
            </div>
          </div>
        )}

        {!isProcessing && (
          <>

            <div
              {...getRootProps()}
              style={{
                padding: 'var(--x-large)',
                marginBottom: 'var(--medium)',
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px dashed ${isDragActive ? 'var(--color-primary)' : '#ddd'}`,
                borderRadius: 8,
                background: isDragActive
                  ? 'var(--color-primary-light, #e3f2fd)'
                  : 'var(--color-neutral-50, #fafafa)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.2s, background 0.2s',
              }}
            >
              <input {...getInputProps()} />
              <p
                style={{
                  margin: 0,
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: 'var(--color-neutral-800)',
                }}
              >
                {isDragActive
                  ? 'Drop your file here'
                  : 'Drop a document here or click to upload'}
              </p>
              <p
                style={{
                  margin: 'var(--x-small) 0 0',
                  fontSize: '0.9rem',
                  color: 'var(--color-neutral-600)',
                }}
              >
                {ACCEPTED_LABEL}
              </p>
            </div>
            {selectedFile && (
              <div style={{
                margin: '-20px 0 var(--medium)',
                display: 'block',
              }}>
                <span style={{ color: 'var(--color-neutral-700)', fontSize: '0.9rem' }}>
                  Selected: {selectedFile.name}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'var(--color-primary)',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginLeft: '8px'
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            <div style={{ marginBottom: 'var(--medium)' }}>
              <label
                htmlFor="ingestion-additional-context"
                style={{
                  display: 'block',
                  marginBottom: 'var(--x-small)',
                  fontWeight: 600,
                  color: 'var(--color-neutral-800)',
                }}
              >
                Additional AI context (optional)
              </label>
              <textarea
                id="ingestion-additional-context"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Add any missing data points, nuanced details, or instructions for how AI should interpret this document."
                rows={4}
                style={{
                  width: '100%',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginTop: 'var(--small)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button
                primary
                disabled={!selectedFile || isProcessing}
                onClick={handleSubmitImport}
              >
                Submit import
              </Button>

            </div>
          </>
        )}
      </div>

      <IngestionHistoryTable projectId={projectId} />
    </>
  );
}
